// HOOKS
import useAgingTicketsReport from "../../hooks/reports/useAgingTickets.report.hook";
import useGetAgingTickets from "../../hooks/reports/useGetAgingTickets.report.hook";
import { useState, useContext, useEffect } from "react";
import useEmployeeReport from "../../hooks/rois/useEmployeeReport.roi";
import useGetManyPaymentsReport from "../../hooks/payments/useGetManyReport.payment";
import usePaymentReport from "../../hooks/payments/usePaymentReport.payment";
import useGetSquareTradeTickets from "../../hooks/reports/useSquareTrade.report.hook";
import useTechHoursReport from "../../hooks/reports/useTechHoursReport";
import useColumns from "../../hooks/other/useColumns.other.hook";
import useDownloadCSV from "../../hooks/other/useDownloadCSV.other.hook";
// COMPONENTS
import DropdownButton from "../../components/buttons/dropdown.button";
import DefaultButton from "../../components/buttons/default.button";
import ReportFields from "../../components/reportFields/reportFields.component";
import ColumnFilter from "../../components/tables/customizableTable/features/columnFilter/columnFilter.jsx";
// CONTEXTS
import { ROIsContext } from "../../contexts/rois/rois.context";
import { ReportsContext } from "../../contexts/reports/reports.context";

const FORM_FIELDS = {
  startDate: "",
  endDate: "",
  report: "",
  filename: "",
  ticketStatuses: [],
  assets: {
    asset_types: [],
  },
  models: [],
};

const CLASSNAMES = {
  dropdownButton: {
    dropdown:
      "border-animate rounded h-[250px] w-[250px] overflow-y-scroll absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    valueContainer:
      "absolute right-[1px] top-[1px] flex items-center justify-center text-center text-xs leading-none min-w-[20px] min-h-[20px] active:drop-shadow group-hover:bg-primary group-hover:text-white rounded-full text-white bg-secondary",
    container:
      "rounded group bg-primary text-white hover:bg-secondary shadow shadow-secondary flex flex-col relative",
  },
  selected: "bg-secondary text-white",
  option:
    "shadow shadow-black font-ubuntuRegular text-black rounded-sm hover:bg-primary flex flex-row justify-between items-center px-2 py-1",
};

const REPORTS = [
  "Employee ROI",
  "Aging Tickets",
  "Store Credit",
  "Square Trade",
];

const TABS = ["CSV Reports", "Tech Hours"];

const STATUS_MESSAGES = [
  "Fetching time entries...",
  "Retrieving ticket details...",
  "Looking up technician names...",
  "Matching customer records...",
  "Cross-referencing billing data...",
  "Calculating billable totals...",
  "Organizing results by ticket...",
  "Almost there...",
];

const getLastMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { startDate: fmt(start), endDate: fmt(end) };
};

const TECH_HOURS_COLUMNS = [
  { id: "user",            label: "Tech Name",       show: true, tdClass: "whitespace-nowrap",
    render: (row)    => row.user || "—",
    text:   (row)    => row.user || "" },
  { id: "start_time",      label: "Start Time",      show: true, tdClass: "whitespace-nowrap text-xs text-gray-500",
    render: (row)    => row.start_time ? new Date(row.start_time).toLocaleString("en-US") : "—",
    text:   (row)    => row.start_time ? new Date(row.start_time).toLocaleString("en-US") : "" },
  { id: "customer_name",   label: "Customer Name",   show: true, tdClass: "",
    render: (row, g) => g ? (row.customer_name || "—") : "",
    text:   (row)    => row.customer_name || "" },
  { id: "contact_name",    label: "Contact Name",    show: true, tdClass: "",
    render: (row, g) => g ? (row.contact_name || "—") : "",
    text:   (row)    => row.contact_name || "" },
  { id: "ticket_number",   label: "Ticket #",        show: true, tdClass: "font-medium",
    render: (row, g) => g ? (row.ticket_number || "—") : "",
    text:   (row)    => row.ticket_number || "" },
  { id: "actual_time",     label: "Actual Time",     show: true, tdClass: "font-mono",
    render: (row)    => row.actual_time || "—",
    text:   (row)    => row.actual_time || "" },
  { id: "billable_time",   label: "Billable Time",   show: true, tdClass: "font-mono",
    render: (row)    => row.billable_time || "—",
    text:   (row)    => row.billable_time || "" },
  { id: "highest_billing", label: "Highest Billing", show: true, tdClass: "",
    render: (row, g) => g && row.top_biller_name ? (
      <span className="text-xs">
        <span className="font-medium">{row.top_biller_name}</span>
        <span className="text-gray-400 font-mono ml-1">({row.top_biller_time})</span>
      </span>
    ) : "",
    text: (row) => row.top_biller_name ? `${row.top_biller_name} (${row.top_biller_time})` : "" },
  { id: "invoice_total",   label: "Invoice Total",   show: true, tdClass: "",
    render: (row, g) => g && row.invoice_total != null && row.invoice_total !== ""
      ? `$${parseFloat(row.invoice_total).toFixed(2)}` : g ? "—" : "",
    text:   (row, g) => g && row.invoice_total != null && row.invoice_total !== ""
      ? parseFloat(row.invoice_total).toFixed(2) : "" },
  { id: "invoice_number",  label: "Invoice #",       show: true, tdClass: "",
    render: (row, g) => g ? row.invoice_number || "—" : "",
    text:   (row, g) => g ? row.invoice_number || "" : "" },
  { id: "billable",        label: "Billable",        show: true, tdClass: "",
    render: (row) => (
      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${row.billable ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
        {row.billable ? "Yes" : "No"}
      </span>
    ),
    text: (row) => row.billable ? "Yes" : "No" },
  { id: "notes",           label: "Notes",           show: true, tdClass: "max-w-[200px] truncate",
    render: (row) => <span title={row.notes || ""}>{row.notes || "—"}</span>,
    text:   (row) => row.notes || "" },
];

const Page = () => {
  const [activeTab, setActiveTab] = useState("CSV Reports");

  // --- Existing hooks (unchanged) ---
  const { payments, setPaymentReportInput } = useGetManyPaymentsReport();
  const { generateAgingTicketsReport } = useAgingTicketsReport();
  const {
    squareTradeTickets,
    setTicketInput: setSquareTradeInput,
    generateSquareTradeReport,
  } = useGetSquareTradeTickets();
  const {
    report: reportData,
    setReport,
    generateReport,
    clearReport,
  } = useContext(ReportsContext);
  const { generateEmployeeReport } = useEmployeeReport();
  const { generatePaymentReport } = usePaymentReport();
  const { agingTickets, setTicketInput } = useGetAgingTickets();
  const { rois, setROIInput } = useContext(ROIsContext);
  const [formFields, setFormFields] = useState(FORM_FIELDS);
  const { startDate, endDate, report, ltDate, statuses } = formFields;
  const [download, setDonwload] = useState(false);

  // --- Tech Hours ---
  const { rows: techRows, runReport, getEstimate, loading: techLoading } = useTechHoursReport();
  const { columns: techColumns, handleColumnFilterCheck } = useColumns(TECH_HOURS_COLUMNS);
  const handleDownload = useDownloadCSV();
  const [techDates, setTechDates] = useState(getLastMonthRange);
  const [techDateError, setTechDateError] = useState("");
  const [billingStatuses, setBillingStatuses] = useState([]);
  const [techRan, setTechRan] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [estimateInfo, setEstimateInfo] = useState(null);

  useEffect(() => {
    if (!techLoading) { setStatusMsg(""); return; }
    setStatusMsg(STATUS_MESSAGES[0]);
    let idx = 1;
    const timer = setInterval(() => {
      setStatusMsg(STATUS_MESSAGES[idx % STATUS_MESSAGES.length]);
      idx++;
    }, 2800);
    return () => clearInterval(timer);
  }, [techLoading]);

  const validateTechDates = (start, end) => {
    if (!start || !end) { setTechDateError(""); return; }
    const maxEnd = new Date(start);
    maxEnd.setMonth(maxEnd.getMonth() + 1);
    if (new Date(end) > maxEnd) {
      setTechDateError("Date range cannot exceed 1 month.");
    } else {
      setTechDateError("");
    }
  };

  const BILLING_STATUS_OPTIONS = [
    { value: "non-billable", label: "Non-Billable" },
    { value: "unbilled",     label: "Unbilled" },
    { value: "billed",       label: "Billed" },
    { value: "invoiced",     label: "Invoiced" },
  ];

  const toggleBillingStatus = (value) => {
    setBillingStatuses((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  // --- Existing handlers (unchanged) ---
  const handleClick = (event) => {
    const { value } = event.target.dataset;
    if (report === value) {
      setFormFields({ ...formFields, report: "" });
      return;
    } else {
      setFormFields({ ...formFields, report: value });
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "startDate") {
      if (endDate && new Date(value) > new Date(endDate)) {
        alert("Start date cannot be greater than end date");
        return;
      }
    }
    if (name === "endDate") {
      if (startDate && new Date(value) < new Date(startDate)) {
        alert("End date cannot be less than start date");
        return;
      }
    }
    setFormFields({ ...formFields, [name]: value });
  };

  const handleRun = () => {
    if (report === "Employee ROI") {
      setROIInput({ qa_date_date_range: { start: startDate, end: endDate } });
    }
    if (report === "Aging Tickets") {
      setTicketInput({
        updated_at_date_range: { start: ltDate },
        statuses,
        assets: {
          asset_types: formFields.assets.asset_types,
        },
      });
    }
    if (report === "Store Credit") {
      setPaymentReportInput({
        created_at_date_range: { start: startDate, end: endDate },
        payment_method: ["Store Credit"],
      });
    }
    if (report === "Square Trade") {
      setSquareTradeInput({
        created_at_date_range: { start: startDate, end: endDate },
        customer_id: 28065436,
        models: formFields.models,
      });
    }
    setDonwload(true);
  };

  useEffect(() => {
    setReport(agingTickets);
  }, [agingTickets]);

  useEffect(() => {
    if (rois.length && download) {
      let newFilename = formFields.filename.split(".")[0];
      if (newFilename === "") newFilename = `${report}`;
      generateEmployeeReport(rois, newFilename);
      clearReport();
    }
    setDonwload(false);
  }, [rois]);

  useEffect(() => {
    if (payments.length) {
      let newFilename = formFields.filename.split(".")[0];
      if (newFilename === "") newFilename = `${report}`;
      generatePaymentReport(payments, newFilename);
    }
    setDonwload(false);
  }, [payments]);

  useEffect(() => {
    if (reportData.length && download) {
      let newFilename = formFields.filename.split(".")[0];
      if (newFilename === "") newFilename = `${report}`;
      generateReport(generateAgingTicketsReport, newFilename);
    }
    setDonwload(false);
  }, [reportData]);

  useEffect(() => {
    if (squareTradeTickets.length && download) {
      let newFilename = formFields.filename.split(".")[0];
      if (newFilename === "") newFilename = `${report}`;
      generateSquareTradeReport(squareTradeTickets, newFilename);
    }
    setDonwload(false);
  }, [squareTradeTickets]);

  // --- Tech Hours handlers ---
  const visCols = techColumns.filter((c) => c.show);

  const handleDownloadCSV = () => {
    let gIdx = -1;
    const data = techRows.map((r, i) => {
      const g = i === 0 || !r.ticket_number || r.ticket_number !== techRows[i - 1].ticket_number;
      if (g) gIdx++;
      return visCols.map((c) => String(c.text(r, g)).replace(/,/g, " "));
    });
    handleDownload({ headers: visCols.map((c) => c.label), data, filename: "Tech Hours Report" });
  };

  const handlePrint = () => {
    const win = window.open("", "_blank");
    const esc = (str) =>
      String(str ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    let groupIndex = -1;
    const rowsHtml = techRows
      .map((row, i) => {
        const isGroupStart =
          i === 0 || !row.ticket_number || row.ticket_number !== techRows[i - 1].ticket_number;
        if (isGroupStart) groupIndex++;
        const bg = groupIndex % 2 === 0 ? "#fff" : "#f8fafc";
        const borderTop = isGroupStart && i !== 0 ? "border-top:2px solid #cbd5e1;" : "";
        const cells = visCols
          .map((c) => `<td>${esc(c.text(row, isGroupStart)) || "—"}</td>`)
          .join("");
        return `<tr style="background:${bg};${borderTop}">${cells}</tr>`;
      })
      .join("");

    const theadHtml = visCols.map((c) => `<th>${esc(c.label)}</th>`).join("");

    win.document.write(`<!DOCTYPE html><html><head>
      <title>Tech Hours Report</title>
      <style>
        body{font-family:sans-serif;font-size:11px;margin:16px}
        h2{font-size:14px;margin-bottom:4px}
        p{font-size:10px;color:#666;margin-bottom:8px}
        table{border-collapse:collapse;width:100%}
        thead{display:table-header-group}
        thead tr{background:#334155;color:#fff}
        th{padding:5px 8px;text-align:left;white-space:nowrap}
        td{padding:4px 8px;border-bottom:1px solid #e2e8f0}
        @media print{@page{margin:1cm}thead{display:table-header-group}}
      </style>
    </head><body>
      <h2>Tech Hours Revenue Report</h2>
      <p>${esc(techDates.startDate)} &ndash; ${esc(techDates.endDate)} &nbsp;|&nbsp; ${techRows.length} record${techRows.length !== 1 ? "s" : ""}</p>
      <table><thead><tr>${theadHtml}</tr></thead><tbody>${rowsHtml}</tbody></table>
    </body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const fmtEstimate = (seconds) => {
    if (seconds < 30) return "under 30 seconds";
    if (seconds < 60) return "under a minute";
    const mins = Math.ceil(seconds / 60);
    return `~${mins} minute${mins !== 1 ? "s" : ""}`;
  };

  const handleTechRun = async () => {
    if (!techDates.startDate || !techDates.endDate) {
      alert("Please select both start and end dates");
      return;
    }
    if (techDateError) return;
    setTechRan(false);
    setEstimateInfo(null);
    try {
      const est = await getEstimate(techDates.startDate, techDates.endDate, billingStatuses);
      if (est) setEstimateInfo(est);
      await runReport(techDates.startDate, techDates.endDate, billingStatuses);
    } catch (err) {
      alert(`Error: ${err?.message}`);
    } finally {
      setTechRan(true);
      setEstimateInfo(null);
    }
  };

  return (
    <div
      style={{ height: "calc(100vh - 136px)" }}
      className="p-4 flex flex-col gap-2 overflow-y-auto"
    >
      <div className="flex justify-center">
        <h1 className="font-ubuntuBold text-xl">Reports</h1>
      </div>

      {/* Tab bar */}
      <div className="flex gap-2 border-b border-slate-400 pb-1">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 rounded-t text-sm font-medium transition-colors ${
              activeTab === tab
                ? "bg-primary text-white"
                : "bg-slate-200 text-gray-700 hover:bg-slate-300"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* CSV Reports tab — existing UI unchanged */}
      {activeTab === "CSV Reports" && (
        <div className="gap-2 p-2 border-2 border-slate-400 justify-center flex flex-col bg-slate-300">
          <div className="flex justify-center">
            <DropdownButton
              classNames={CLASSNAMES.dropdownButton}
              label={report || "Report"}
            >
              {REPORTS.map((item, index) => (
                <div
                  onClick={handleClick}
                  data-value={item}
                  key={index}
                  className={`${
                    report === item ? CLASSNAMES.selected : "bg-slate-100"
                  } ${CLASSNAMES.option}`}
                >
                  {item}
                </div>
              ))}
            </DropdownButton>
          </div>
          {report && (
            <ReportFields
              handleChange={handleChange}
              report={report}
              formFields={formFields}
              setFormFields={setFormFields}
            />
          )}
          {report && (
            <div className="flex justify-center">
              <DefaultButton loading={download} handleClick={handleRun}>
                Download
              </DefaultButton>
            </div>
          )}
        </div>
      )}

      {/* Tech Hours tab */}
      {activeTab === "Tech Hours" && (
        <div className="flex flex-col gap-3 min-h-0">
          {/* Controls */}
          <div className="p-4 border-2 border-slate-400 bg-slate-300 flex flex-col gap-3 flex-shrink-0">
            <div className="text-center">
              <h2 className="font-ubuntuBold text-base">Tech Hours Revenue Report</h2>
              <p className="text-xs text-gray-500 mt-0.5">Maximum date range: 1 month</p>
            </div>

            {/* Date range + action buttons */}
            <div className="flex flex-wrap gap-4 justify-center items-end">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-ubuntuRegular text-gray-600">Start Date</label>
                <input
                  type="date"
                  value={techDates.startDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTechDates((p) => ({ ...p, startDate: val }));
                    validateTechDates(val, techDates.endDate);
                  }}
                  className={`rounded p-2 text-sm ${techDateError ? "border-2 border-red-400" : ""}`}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-ubuntuRegular text-gray-600">End Date</label>
                <input
                  type="date"
                  value={techDates.endDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTechDates((p) => ({ ...p, endDate: val }));
                    validateTechDates(techDates.startDate, val);
                  }}
                  className={`rounded p-2 text-sm ${techDateError ? "border-2 border-red-400" : ""}`}
                />
              </div>
              <DefaultButton loading={techLoading} handleClick={handleTechRun} disabled={!!techDateError}>
                Run Report
              </DefaultButton>
              {techRows.length > 0 && (
                <>
                  <DefaultButton handleClick={handleDownloadCSV}>
                    Download CSV
                  </DefaultButton>
                  <DefaultButton handleClick={handlePrint}>
                    Print
                  </DefaultButton>
                </>
              )}
              <ColumnFilter
                columns={techColumns}
                handleChange={handleColumnFilterCheck}
                classNames={{ dropdownButton: CLASSNAMES.dropdownButton }}
              />
            </div>
            {techDateError && (
              <p className="text-xs text-red-600 text-center font-ubuntuRegular">{techDateError}</p>
            )}

            {/* Billing status filter */}
            <div className="flex flex-col gap-1" style={{ display: "none" }}>
              <span className="text-xs font-ubuntuBold text-gray-600 text-center">
                Billing Status{" "}
                <span className="font-ubuntuRegular text-gray-400">
                  (leave all unchecked to include all)
                </span>
              </span>
              <div className="flex flex-wrap gap-3 justify-center">
                {BILLING_STATUS_OPTIONS.map(({ value, label }) => (
                  <label
                    key={value}
                    className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      checked={billingStatuses.includes(value)}
                      onChange={() => toggleBillingStatus(value)}
                      className="accent-primary"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Loading status */}
          {techLoading && (
            <div className="flex flex-col items-center gap-3 py-6">
              {estimateInfo && (
                <div className="text-center bg-slate-100 border border-slate-300 rounded-lg px-5 py-3">
                  <p className="text-sm font-ubuntuBold text-gray-700">
                    {estimateInfo.total_pages} page{estimateInfo.total_pages !== 1 ? "s" : ""} of data found
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Estimated time: {fmtEstimate(estimateInfo.estimated_seconds)}
                  </p>
                </div>
              )}
              <div className="flex gap-1.5">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="w-2 h-2 rounded-full bg-primary animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-600 font-ubuntuRegular transition-all duration-500">
                {statusMsg}
              </span>
            </div>
          )}

          {/* Results */}
          {techRan && !techLoading && (
            techRows.length === 0 ? (
              <p className="text-center text-gray-500 text-sm py-6 flex-shrink-0">
                No records found for the selected date range.
              </p>
            ) : (
              <div className="flex flex-col min-h-0 rounded border border-slate-300 shadow-sm overflow-hidden flex-shrink-0">
                <div className="flex items-center justify-between px-4 py-2 bg-slate-200 border-b border-slate-300 flex-shrink-0">
                  <span className="text-xs text-gray-500 font-ubuntuRegular">
                    {techRows.length} row{techRows.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 460px)" }}>
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-700 text-white sticky top-0 z-10">
                      <tr>
                        {visCols.map((col) => (
                          <th key={col.id} className="px-3 py-2 font-ubuntuBold whitespace-nowrap">
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let groupIndex = -1;
                        return techRows.map((row, i) => {
                          const isGroupStart = i === 0 || !row.ticket_number || row.ticket_number !== techRows[i - 1].ticket_number;
                          if (isGroupStart) groupIndex++;
                          const bg = groupIndex % 2 === 0 ? "bg-white" : "bg-slate-50";
                          const borderTop = isGroupStart && i !== 0 ? "border-t-2 border-slate-300" : "";
                          return (
                            <tr key={i} className={`${bg} ${borderTop}`}>
                              {visCols.map((col) => (
                                <td key={col.id} className={`px-3 py-1.5 ${col.tdClass || ""}`}>
                                  {col.render(row, isGroupStart)}
                                </td>
                              ))}
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default Page;
